import {Box, Popover} from '@mui/material';
import {mergeAttributes, Node} from '@tiptap/core';
import {NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps} from '@tiptap/react';
import {createContext, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {inlineVariableChipSx} from '../../../../../common/variables/variableChipStyles';
import type {AvailableVariable} from '../../../config/nodeVariables';
import AutomationVariablePicker from './AutomationVariablePicker';
import {automationVariableToken, type AutomationVariableReference} from '../../../config/variableReferences';
import type {EmailPlaceholder} from '../../../config/emailPlaceholders';
import {referenceOptions} from './automationVariableOptions';

export const AutomationVariableContext = createContext<{
    variables: AvailableVariable[]; placeholders: EmailPlaceholder[]; disabled: boolean;
}>({
    variables: [], placeholders: [], disabled: false,
});

function AutomationVariableNodeView({node, editor, selected, getPos, updateAttributes}: NodeViewProps) {
    const {t} = useTranslation();
    const {variables, disabled} = useContext(AutomationVariableContext);
    const reference = node.attrs as AutomationVariableReference;
    const [picker, setPicker] = useState<{anchor: HTMLElement; part: 'source' | 'field'} | null>(null);
    const sourceExists = variables.some(variable => variable.nodeName === reference.nodeName);
    const options = variables.filter(variable => picker?.part === 'source'
        ? variable.name === reference.name
        : variable.nodeName === reference.nodeName);

    const openPicker = (anchor: HTMLElement, part: 'source' | 'field') => {
        if (disabled) return;
        const position = getPos();
        if (typeof position === 'number') editor.commands.setNodeSelection(position);
        setPicker({anchor, part});
    };

    const closePicker = () => {
        setPicker(null);
        editor.commands.focus();
    };

    const segment = (part: 'source' | 'field', label: string) => (
        <Box
            component={'button'} type={'button'} disabled={disabled}
            aria-label={`${t(part === 'source' ? 'Source' : 'Field')}: ${label}`}
            title={label}
            onMouseDown={event => event.preventDefault()}
            onClick={event => {
                event.preventDefault();
                openPicker(event.currentTarget, part);
            }}
            sx={{
                ...inlineVariableChipSx,
                px: '0.35em', py: '0.12em', border: 0, fontSize: 'inherit', fontFamily: 'inherit',
                lineHeight: 'inherit', maxWidth: 'min(20em, 38vw)', overflow: 'hidden', textOverflow: 'ellipsis',
                cursor: disabled ? 'default' : 'pointer',
                '&:hover, &:focus-visible': {bgcolor: '#EDE9FE', outline: 'none'},
            }}
        >{label}</Box>
    );

    return (
        <NodeViewWrapper as={'span'} style={{whiteSpace: 'nowrap'}}>
            <Box component={'span'} contentEditable={false} data-automation-variable={automationVariableToken(reference)}
                 sx={{
                     display: 'inline-flex', alignItems: 'baseline', gap: '3px', verticalAlign: 'baseline',
                     mx: '2px', borderRadius: '4px',
                     outline: selected || picker ? '2px solid' : '2px solid transparent',
                     outlineColor: selected || picker ? (sourceExists ? '#7C3AED' : 'error.main') : 'transparent',
                     outlineOffset: '1px',
                     '&:hover, &:focus-within': {outlineColor: sourceExists ? '#7C3AED' : 'error.main'},
                 }}>
                {segment('source', reference.nodeName)}
                <Box component={'span'} aria-hidden sx={{color: inlineVariableChipSx.color}}>·</Box>
                {segment('field', reference.name)}
            </Box>
            <Popover open={!disabled && !!picker} anchorEl={picker?.anchor} onClose={closePicker}
                     disableRestoreFocus transitionDuration={0}
                     anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                     slotProps={{paper: {sx: {mt: 1, borderRadius: 2}}}}>
                <AutomationVariablePicker options={referenceOptions(options)}
                                          purpose={picker?.part === 'source' ? 'change-source' : 'select-step-value'}
                                          onSelect={option => {
                                              if (option.kind !== 'reference') return;
                                              updateAttributes({nodeName: option.variable.nodeName, name: option.variable.name});
                                              closePicker();
                                          }}/>
            </Popover>
        </NodeViewWrapper>
    );
}

/** Both visual segments are one atom, so deletion, selection and copying keep the reference whole. */
const AutomationVariableNode = Node.create({
    name: 'automationVariable',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,
    addAttributes() {
        return {
            nodeName: {default: ''},
            name: {default: ''},
        };
    },
    // Clipboard HTML also carries the literal token; persisted email content never uses this HTML.
    renderHTML({node, HTMLAttributes}) {
        return ['span', mergeAttributes(HTMLAttributes), automationVariableToken(node.attrs as AutomationVariableReference)];
    },
    renderText({node}) {
        return automationVariableToken(node.attrs as AutomationVariableReference);
    },
    addNodeView() {
        return ReactNodeViewRenderer(AutomationVariableNodeView);
    },
});

export default AutomationVariableNode;
